import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, FalhaDaApi } from '../api/cliente';
import { Alerta, Botao } from './base';
import { Modal } from './Modal';

interface ResumoDaExclusao {
  readonly nome: string;
  readonly documentos: number;
  readonly interacoes: number;
  readonly acordos: number;
  readonly podeExcluir: boolean;
  readonly impedimento: { mensagem: string; codigo: string } | null;
}

/**
 * Exclusão definitiva do cadastro — só no sistema do escritório.
 *
 * A clínica NÃO exclui cliente pelo portal, e isso é deliberado: ela cadastra e
 * anexa, o escritório decide o que sai. Não há rota de exclusão de devedor no
 * portal, então aqui é o único caminho.
 *
 * A confirmação mostra o que vai junto — documentos, histórico, acordo sem
 * pagamento —, em vez de um "tem certeza?" que não informa nada. Caso com
 * pagamento (ou custas já apuradas) o servidor recusa, e o botão nem é oferecido:
 * apagar mudaria a prestação de contas de um mês já fechado.
 */
export function ExcluirDevedor({ devedorId, nome }: { devedorId: string; nome: string }) {
  const navegar = useNavigate();
  const clienteConsulta = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  /* O resumo só é buscado quando a janela abre: é uma pergunta sobre uma ação rara,
     e não vale uma consulta a mais em toda abertura de ficha. */
  const { data: resumo, isPending } = useQuery<ResumoDaExclusao>({
    queryKey: ['devedor', devedorId, 'exclusao'],
    queryFn: () => api.buscar<ResumoDaExclusao>(`/devedores/${devedorId}/exclusao`),
    enabled: confirmando,
  });

  const excluir = useMutation({
    mutationFn: () => api.remover(`/devedores/${devedorId}`),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['devedores'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
      navegar('/devedores');
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível excluir o cadastro.'),
  });

  function fechar() {
    setConfirmando(false);
    setErro(null);
  }

  return (
    <>
      {/* Botão neutro, como "Romper acordo": nesta interface a cor significa estado
          do dinheiro (recebido, em atraso), não gravidade do botão. Quem avisa do
          peso da ação é a janela de confirmação, que lista o que vai junto.

          `py-2` casa a altura com o seletor de fase ao lado — os dois usam a mesma
          moldura de campo. */}
      <Botao onClick={() => setConfirmando(true)} className="shrink-0 py-2">
        Excluir cadastro
      </Botao>

      <Modal
        aberto={confirmando}
        aoFechar={fechar}
        titulo={`Excluir ${nome}?`}
        descricao="A exclusão é definitiva — não há como desfazer nem recuperar depois."
        largura="30rem"
        rodape={
          <>
            <Botao onClick={fechar} desabilitado={excluir.isPending}>
              Cancelar
            </Botao>
            {resumo?.podeExcluir && (
              <Botao
                tipo="primario"
                onClick={() => excluir.mutate()}
                desabilitado={excluir.isPending}
              >
                {excluir.isPending ? 'Excluindo…' : 'Excluir definitivamente'}
              </Botao>
            )}
          </>
        }
      >
        <div className="space-y-4">
          {erro && <Alerta tom="vencido">{erro}</Alerta>}

          {isPending && !resumo ? (
            <p className="text-sm text-[color:var(--texto-tenue)]">Conferindo o cadastro…</p>
          ) : resumo?.podeExcluir ? (
            <>
              <p className="text-sm text-[color:var(--texto)]">Vão ser apagados junto:</p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-[color:var(--texto-suave)]">
                <li>
                  {resumo.documentos === 0
                    ? 'Nenhum documento anexado'
                    : `${resumo.documentos} documento(s) anexado(s) — inclusive os que a clínica enviou`}
                </li>
                <li>{resumo.interacoes} registro(s) do histórico e das atualizações</li>
                {resumo.acordos > 0 && (
                  <li>
                    {resumo.acordos} acordo(s) e as parcelas, que ainda não têm pagamento
                    registrado
                  </li>
                )}
              </ul>
            </>
          ) : (
            <Alerta tom="atencao">
              {resumo?.impedimento?.mensagem ?? 'Este caso não pode ser excluído.'}
            </Alerta>
          )}
        </div>
      </Modal>
    </>
  );
}
